"use client";

import React, { useEffect, useId, useRef, useState } from "react";
import { ChatBubble } from "./chat-bubble";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export const CHAT_TRUNCATE_TOGGLE_BUTTON_CLASS =
  "min-w-[72px] h-6 px-3 text-[12px] font-medium bg-muted text-muted-foreground";

const USER_COLLAPSED_MAX_HEIGHT_PX = 420;
const USER_CHARACTER_LIMIT = 1200;

export function TruncatableUserBubble({ text }: { text: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentId = useId();

  useEffect(() => {
    setExpanded(false);
  }, [text]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const updateOverflow = () => {
      setIsOverflowing(element.scrollHeight > USER_COLLAPSED_MAX_HEIGHT_PX);
    };

    updateOverflow();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(() => updateOverflow());
      observer.observe(element);
      return () => observer.disconnect();
    }
    return;
  }, [text]);

  const hasLongContent = text.length >= USER_CHARACTER_LIMIT || isOverflowing;
  const shouldClamp = hasLongContent && !expanded;
  const showToggle = hasLongContent;
  const toggleLabel = expanded ? "Show less" : "Show more";

  return (
    <div className="relative flex flex-col items-end pb-4">
      <ChatBubble role="user" className={cn("relative", showToggle && "pb-0")}>
        <div
          id={contentId}
          ref={containerRef}
          className={cn(
            "whitespace-pre-wrap break-words",
            showToggle && "pb-4",
            shouldClamp && "max-h-[420px] overflow-hidden relative",
          )}
          style={{
            maxHeight: shouldClamp
              ? `${USER_COLLAPSED_MAX_HEIGHT_PX}px`
              : "none",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          }}
        >
          {text.split("\n").map((line, idx) => (
            <React.Fragment key={idx}>
              {line}
              <br />
            </React.Fragment>
          ))}
        </div>
        <div
          className={cn(
            "h-30 w-full absolute bottom-0 left-0 bg-gradient-to-b from-transparent to-muted z-10 rounded-b-lg pointer-events-none",
            !shouldClamp && "hidden",
          )}
          aria-hidden="true"
        ></div>
        {showToggle && (
          <Button
            type="button"
            size="icon"
            variant="outline"
            className={cn(
              "absolute bottom-0 left-1/2 translate-x-[-50%] translate-y-1/2 z-20",
              CHAT_TRUNCATE_TOGGLE_BUTTON_CLASS,
            )}
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
            aria-controls={contentId}
          >
            {toggleLabel}
          </Button>
        )}
      </ChatBubble>
    </div>
  );
}
